import React, { useState } from "react";
import { Box, Chip, Collapse, Paper, Typography, alpha } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * Section card used by every signage create/edit page so the
 * Devices / Playlists / Media forms feel identical to Product Create.
 *
 * Lifted from Pages/Product/Create/index.tsx so the visual language
 * stays in lockstep.
 */
export interface SignageSectionProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  badge?: string;
  accentColor?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Optional action slot rendered in the header (e.g. an "Add" button). */
  right?: React.ReactNode;
  children: React.ReactNode;
}

const SignageSection: React.FC<SignageSectionProps> = ({
  icon,
  title,
  subtitle,
  badge,
  accentColor = "#00838f",
  collapsible,
  defaultOpen = true,
  right,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          borderColor: "#cbd5e1",
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.04)} 0%, ${alpha(accentColor, 0.01)} 100%)`,
          borderBottom: open ? `1px solid ${alpha(accentColor, 0.08)}` : "none",
          px: 3,
          py: 2.5,
          cursor: collapsible ? "pointer" : "default",
          transition: "all 0.2s ease",
        }}
        onClick={() => collapsible && setOpen((v) => !v)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${alpha(accentColor, 0.12)} 0%, ${alpha(accentColor, 0.06)} 100%)`,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                fontSize={15}
                fontWeight={700}
                color="#0f172a"
                letterSpacing="-0.01em"
              >
                {title}
              </Typography>
              {badge && (
                <Chip
                  label={badge}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 9,
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                    color: "#dc2626",
                    border: "none",
                  }}
                />
              )}
            </Box>
            {subtitle && (
              <Typography fontSize={11} color="#94a3b8" mt={0.25}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {right && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {right}
            </Box>
          )}
          {collapsible && (
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: "#94a3b8",
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: open ? "rotate(180deg)" : "rotate(0)",
              }}
            />
          )}
        </Box>
      </Box>
      {collapsible ? (
        <Collapse in={open}>
          <Box sx={{ p: 3 }}>{children}</Box>
        </Collapse>
      ) : (
        <Box sx={{ p: 3 }}>{children}</Box>
      )}
    </Paper>
  );
};

export const signageLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  mb: 0.75,
  letterSpacing: "0.02em",
  textTransform: "uppercase" as const,
};

export const signageInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    fontSize: 13,
    bgcolor: "#fff",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e2e8f0", transition: "all 0.2s ease" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 2 },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,131,143,0.08)" },
  },
};

export default SignageSection;
